/** Mirrors `v1_admin_KioskRelease`. */
export interface KioskRelease {
  id: string;
  version_code: number;
  version_name: string;
  package_name: string;
  sha256: string;
  size_bytes: number;
  notes: string;
  uploaded_by_email?: string | null;
  created_at: string;
  installed_device_count: number;
  open_update_count: number;
}

/** Mirrors `v1_admin_DeviceKioskUpdate`. */
export interface DeviceKioskUpdate {
  id: string;
  device_id: string;
  release_id: string;
  version_code: number;
  version_name: string;
  status: string;
  detail?: string;
  from_version_code?: number;
  scheduling_mode?: string;
  forced_by?: string;
  forced_at?: string;
  install_trigger?: string;
  requested_by_email?: string;
  created_at: string;
  updated_at: string;
  install_started_at?: string;
  completed_at?: string;
}

/** Statuses in which the row is still the device's target. Mirrors the server's list. */
export const OPEN_UPDATE_STATUSES = new Set([
  "pending",
  "downloading",
  "staged",
  "installing",
  "verifying",
]);

export const APK_CONTENT_TYPE = "application/vnd.android.package-archive";

export function formatBytes(n: number): string {
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(0)} KB`;
  return `${(n / (1024 * 1024)).toFixed(1)} MB`;
}

export function formatWhen(iso: string | undefined): string {
  if (!iso) return "—";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  // Friendly UK wall time (see docs/frontend.md § Dates) — not the browser locale.
  return d.toLocaleString("en-GB", {
    day: "numeric",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    hourCycle: "h23",
  });
}

export function updateStatusClasses(status: string): string {
  if (status === "succeeded") return "text-green-400";
  if (status === "failed") return "text-red-400";
  if (status === "cancelled") return "text-text-secondary";
  return "text-yellow-400";
}

/** What each status means for the operator reading it. */
export function describeUpdateStatus(status: string): string {
  switch (status) {
    case "pending":
      return "Queued. The table picks it up on its next heartbeat.";
    case "downloading":
      return "The table is downloading the APK in the background. Play is unaffected.";
    case "staged":
      return "Downloaded and verified. Waiting for startup, scheduled downtime or an explicit force update.";
    case "installing":
      return "Install authorised. Payments are paused until the new build verifies its command channel.";
    case "verifying":
      return "The new build started. Waiting for it to execute and acknowledge a remote command.";
    case "succeeded":
      return "The new build proved remote command execution works.";
    case "failed":
      return "The update failed or could not be verified. See the detail; recovery may be required.";
    case "cancelled":
      return "Withdrawn before it installed.";
    default:
      return status;
  }
}
